import React, { useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { XCloseIcon } from '@/components/icons';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { DialogContext } from '../Header';

interface CollabEmailConfirmDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onAccept: () => void;
    dialogType?: 'send' | 'accept';
}

const CollabEmailConfirmDialog: React.FC<CollabEmailConfirmDialogProps> = ({
    isOpen,
    onClose,
    onAccept,
    dialogType = 'accept',
}) => {
    const user = useSelector((state: RootState) => state.auth.user);
    const email = user?.email || '';
    // Access the global dialog context
    const { setIsDialogOpen } = useContext(DialogContext);

    // Update the global dialog state based on this dialog's open state
    useEffect(() => {
        if (isOpen) {
            // Set global dialog state to open when this dialog opens
            setIsDialogOpen(true);
            document.body.style.overflow = 'hidden';
        } else {
            // Set global dialog state to closed when this dialog closes
            setIsDialogOpen(false);
            document.body.style.overflow = '';
        }

        return () => {
            // Reset global dialog state when component unmounts
            if (isOpen) {
                setIsDialogOpen(false);
            }
            document.body.style.overflow = '';
        };
    }, [isOpen, email, setIsDialogOpen]);

    if (!isOpen) return null;

    // Custom close handler to ensure global state is updated
    const handleClose = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
    };

    // Custom accept handler to ensure global state is updated
    const handleAccept = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onAccept();
    };

    const getDialogTitle = () => {
        return dialogType === 'send' ? 'Send a Collaboration Request' : 'Share Your Email';
    };

    const getAcceptButtonText = () => {
        return dialogType === 'send' ? 'Send Request & Share Info' : 'Accept & Share Info';
    };

    // Use React Portal to render the dialog directly to the body element
    // This ensures it's not affected by parent container events or CSS
    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[10001] flex items-center justify-center"
            onMouseDown={(e) => e.stopPropagation()}>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/80 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal Content */}
            <div
                className="bg-white rounded-lg shadow-lg max-w-md font-sans p-6 relative z-10"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={handleClose}
                    className="absolute right-4 top-4 p-1.5 border border-gray-300 rounded-full cursor-pointer h-7 w-7 flex items-center justify-center"
                >
                    <XCloseIcon width={14} height={14} />
                </button>

                <div className="mb-4">
                    <h2 className="text-xl font-bold text-gray-900">
                        {getDialogTitle()}
                    </h2>
                </div>

                <div className="mt-4">
                    <p className="text-gray-700 mb-4">
                        {dialogType === 'send' ? 'If they accept the following will be shared:' : 'The following will be shared:'}
                    </p>
                    <h3 className="text-gray-900 font-semibold mb-2">Email Address</h3>
                    <div className="bg-gray-100 p-3 rounded-md text-gray-900 font-mono mb-4">
                        {email}
                    </div>
                    <p className="text-gray-600 text-sm">
                        This allows collaborators to contact you directly to coordinate.
                    </p>
                    <p className="text-amber-800 text-sm mt-2 font-medium">
                        Warning: For payments be sure to use buyer/seller protection (e.g. PayPal Goods & Services).{' '}
                        <Link
                            to="/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#4A67E3] hover:underline font-normal"
                        >
                            See Terms for more info
                        </Link>
                    </p>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md font-nunito transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleAccept}
                        className="px-4 py-2 bg-[#5E7DEB] hover:bg-blue-600 text-white rounded-md font-nunito transition-colors"
                    >
                        {getAcceptButtonText()}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default CollabEmailConfirmDialog;
